import React, { useMemo, useState } from 'react';
import { useAuth } from '../AuthComponent';
import { SearchIcon, RefreshIcon } from '@shopify/polaris-icons';
import { Ic, StatusPill } from '../ui';

const ADMIN_PRODUCT_URL = 'https://admin.shopify.com/store/soccerpostshop/products/';
const idFromGid = (gid) => String(gid || '').split('/').pop();

const GROUPS = [
  ['attention', 'Needs attention', 'can'],
  ['waiting', 'Waiting on open orders', 'hold'],
  ['ok', 'OK', 'ful'],
  ['ignored', 'Ignored', 'neu'],
];
const GROUP_NOTE = {
  attention: 'Prepare fixes most of these; it only changes what’s off.',
  waiting: 'The nightly retry unstocks these once the orders ship. Nothing to do.',
  ignored: 'Listed but not checked, e.g. legacy-model products.',
};
const COLLAPSED_BY_DEFAULT = { ok: true, ignored: true };

function formatDateTime(value) {
  if (!value) return '';
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? '' : d.toLocaleString([], { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
}

function Findings({ row }) {
  if (row.group === 'ignored') {
    const ig = row.ignored;
    return <div className="uh-tp-fchips"><span className="uh-tp-fchip">{ig ? `Ignored${ig.reason ? `: ${ig.reason}` : ''} · ${ig.by}` : 'Ignored'}</span></div>;
  }
  if (!row.findings.length) return <div className="uh-tp-fchips"><span className="uh-tp-fchip ok"><span className="d" />Matches the Teamatical setup</span></div>;
  return (
    <div className="uh-tp-fchips">
      {row.findings.map((f) => <span key={f.code + f.text} className={`uh-tp-fchip ${f.level}`}><span className="d" />{f.text}</span>)}
    </div>
  );
}

export default function AuditPanel({ data, loading, error, running, busyId, onRun, onIgnore, onUnignore, onFix, onRetry }) {
  const { nameOf } = useAuth();
  const [filter, setFilter] = useState('all');
  const [search, setSearch] = useState('');
  const [open, setOpen] = useState({});
  const audit = data?.audit;
  const rows = useMemo(() => audit?.rows || [], [audit]);
  const counts = audit?.counts || {};

  const byGroup = useMemo(() => {
    const q = search.trim().toLowerCase();
    const out = {};
    for (const r of rows) {
      if (q && !`${r.title} ${r.teamatical_id || ''}`.toLowerCase().includes(q)) continue;
      (out[r.group] = out[r.group] || []).push(r);
    }
    return out;
  }, [rows, search]);

  const isOpen = (g) => (open[g] ?? (!COLLAPSED_BY_DEFAULT[g] || filter === g || !!search.trim()));

  const actions = (r) => {
    const busy = busyId === r.product_id;
    const shopLink = <a className="uh-btn slim" href={ADMIN_PRODUCT_URL + idFromGid(r.product_id)} target="_blank" rel="noopener noreferrer">Open in Shopify</a>;
    if (r.group === 'ignored') {
      return <>{shopLink}<button type="button" className="uh-btn slim" disabled={busy} onClick={() => onUnignore(r)}>Stop ignoring</button></>;
    }
    return (
      <>
        {shopLink}
        {r.group === 'attention' && r.teamatical_id && <button type="button" className="uh-btn slim" onClick={() => onFix(r)}>Fix with Prepare</button>}
        {r.group === 'waiting' && r.run && <button type="button" className={`uh-btn slim${busy ? ' busy' : ''}`} disabled={busy} onClick={() => onRetry(r)}>{busy ? 'Retrying…' : 'Retry now'}</button>}
        {r.group !== 'waiting' && <button type="button" className="uh-btn slim" disabled={busy} onClick={() => onIgnore(r)}>Ignore</button>}
      </>
    );
  };

  return (
    <div className="uh-card">
      <div className="uh-card-h">
        <div>
          <h2 className="uh-h2 lg">Audit</h2>
          <div className="uh-sub">
            Checks every Teamatical product (archived ones excluded) against the setup this tool applies. Runs nightly after the cleanup retry.
            {audit?.finished_at && <> Last run <b>{formatDateTime(audit.finished_at)}</b> by {nameOf(audit.started_by)} · {audit.product_count} products · {audit.seconds}s.</>}
          </div>
        </div>
        <span className="uh-sp" />
        <div className="uh-search">
          <Ic source={SearchIcon} sm />
          <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search title or Teamatical ID" aria-label="Search audit" />
        </div>
        <button type="button" className={`uh-btn${running ? ' busy' : ''}`} disabled={running} onClick={onRun}>
          <Ic source={RefreshIcon} sm />{running ? 'Running…' : 'Run audit now'}
        </button>
      </div>

      <div className="uh-card-b uh-col" style={{ gap: 12 }}>
        {error && <div className="uh-alert crit"><div>{error}</div></div>}
        {audit?.teamatical_lookup_errors > 0 && (
          <div className="uh-alert"><div>Teamatical didn’t answer for {audit.teamatical_lookup_errors} product{audit.teamatical_lookup_errors === 1 ? '' : 's'}, so their Teamatical checks (design still exists, sizes) were skipped this run.</div></div>
        )}
        {audit?.last_failure && <div className="uh-alert crit"><div>The last audit ({formatDateTime(audit.last_failure.started_at)}) failed: {audit.last_failure.error}. Showing the one before it.</div></div>}
        <div className="uh-tp-astats">
          {GROUPS.map(([g, label, tone]) => (
            <button key={g} type="button" className="uh-stat" aria-pressed={filter === g}
              onClick={() => setFilter(filter === g ? 'all' : g)}>
              <span className="v">{counts[g] || 0}</span>
              <span className="k"><span className={`uh-tp-dot ${tone}`} />{label}</span>
            </button>
          ))}
        </div>
        <div className="uh-help">
          Checks: Teamatical ID set, used by one product only, and still on Teamatical · every Shopify size made by Teamatical · playerNumber = <b>Number</b>, playerName = <b>Name</b> · vendor Teamatical ·
          tracked, continue selling · every variant stocked at {audit?.location_name || 'the Teamatical location'} and in the Teamatical shipping profile ·
          no other stocked locations (open orders mean waiting, not a problem). The Teamatical Name and Number add-on products are skipped.
        </div>
      </div>

      {loading ? <div className="uh-empty">Loading audit…</div>
        : !audit ? <div className="uh-empty">No audit yet. Run one now, or wait for tonight’s.</div>
        : (
          <div className="uh-tscroll">
            <table className="uh-tbl">
              <thead><tr><th>Product</th><th>Teamatical ID</th><th className="r">Variants</th><th>Findings</th><th /></tr></thead>
              <tbody>
                {GROUPS.filter(([g]) => (filter === 'all' || filter === g) && byGroup[g]?.length).map(([g, label, tone]) => (
                  <React.Fragment key={g}>
                    <tr className="uh-tp-grp">
                      <td colSpan={5}>
                        <StatusPill tone={tone}>{label}</StatusPill> <span className="uh-num">{byGroup[g].length}</span>
                        <button type="button" className="uh-link" onClick={() => setOpen({ ...open, [g]: !isOpen(g) })}>{isOpen(g) ? 'Hide' : 'Show'}</button>
                        {GROUP_NOTE[g] && <span className="uh-help">{GROUP_NOTE[g]}</span>}
                      </td>
                    </tr>
                    {isOpen(g) && byGroup[g].map((r) => (
                      <tr key={r.product_id}>
                        <td><div className="nm">{r.title}</div><div className="sm">{r.status.toLowerCase()}</div></td>
                        <td>{r.teamatical_id ? <code className="uh-tp-code">{r.teamatical_id}</code> : <span className="uh-faint">none</span>}</td>
                        <td className="r uh-num">{r.variant_count}</td>
                        <td><Findings row={r} /></td>
                        <td className="uh-tp-acts">{actions(r)}</td>
                      </tr>
                    ))}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          </div>
        )}
    </div>
  );
}
